import { SelectionUtils } from "@/utils/SelectionUtils";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import InsertLink from "@mui/icons-material/InsertLink";
import LinkOff from "@mui/icons-material/LinkOff";
import {
    Card,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
} from "@mui/material";
import Button from "@mui/material/Button";
import { createRef, useEffect, useRef, useState } from "react";
import { setSelection } from "./context/InlineEditorReducer";
import useInlineEditorContext from "./context/useInlineEditorContext";

const InlineEditor = () => {
    const { state, dispatch } = useInlineEditorContext();

    const [selectionVisible, setSelectionVisible] = useState(false);
    const textRef = createRef<HTMLInputElement>();
    const selectionUtils = useRef<SelectionUtils>(new SelectionUtils());
    const [buttonState, setButtonState] = useState<Array<string>>([]);
    const [pos, setPos] = useState<{ x: number; y: number }>({ x: 0, y: 0 });

    const [insertLinkModal, setInsertLinkModal] = useState<boolean>(false);
    const insertLinkModalRef = useRef<boolean>(insertLinkModal);

    useEffect(() => {
        document.addEventListener("selectionchange", onSelectionChange, true);

        return () => {
            document.removeEventListener("selectionchange", onSelectionChange, true);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        insertLinkModalRef.current = insertLinkModal;
    }, [insertLinkModal]);

    const onSelectionChange = () => {
        if (insertLinkModalRef.current) return;

        const activeSelection = SelectionUtils.get();
        if (!activeSelection) return;

        const baseNode = activeSelection.anchorNode as HTMLElement;
        if (!baseNode) return;

        const baseNodeParent = baseNode.parentElement as HTMLElement;
        if (
            (baseNode.closest && baseNode.closest(".InlineEditor")) ||
            (baseNodeParent.closest && baseNodeParent.closest(".InlineEditor"))
        ) {
            return;
        }

        if (
            activeSelection.type === "Range" &&
            activeSelection.rangeCount > 0 &&
            baseNodeParent.closest &&
            baseNodeParent.closest(":not(.cdx-block)[contenteditable]")
        ) {
            selectionUtils.current.save();

            dispatch(setSelection(true));
            setSelectionVisible(true);
            updatePosition();
        } else {
            selectionUtils.current.clearSaved();

            dispatch(setSelection(false));
            setSelectionVisible(false);
        }
    };

    const updatePosition = () => {
        if (insertLinkModalRef.current) return;

        const activeSelection = SelectionUtils.get();
        if (!activeSelection) return;

        const selectionRange = SelectionUtils.range!;
        const selectionRect = selectionRange.getBoundingClientRect();
        const documentRect = document.body.getBoundingClientRect();
        let posX = selectionRect.x + selectionRect.width / 2 - 60;
        posX = Math.max(299, posX);
        const posY = selectionRect.top - documentRect.y + selectionRect.height + 5;

        setPos({ x: posX, y: posY });
        updateState();
    };

    const updateState = () => {
        const bold = document.queryCommandState("bold");
        const italic = document.queryCommandState("italic");
        const link = new SelectionUtils().findParentTag("A");

        const selection = [];
        if (bold) selection.push("bold");
        if (italic) selection.push("italic");
        if (link) selection.push("link");

        setButtonState(selection);
    };

    const toggleBold = (): void => {
        document.execCommand("bold");
        updateState();
    };

    const toggleItalic = (): void => {
        document.execCommand("italic");
        updateState();
    };

    const insertLink = (link: string): void => {
        const anchorTag = selectionUtils.current.findParentTag("A");
        selectionUtils.current.restore();

        if (anchorTag) {
            selectionUtils.current.expandToTag(anchorTag);
        }

        document.execCommand("createLink", false, "ids-platform-inserted-link");
        const newLink = document.querySelector("a[href='ids-platform-inserted-link']") as HTMLLinkElement;
        if (!newLink) {
            removeLink();
            return;
        }

        newLink.href = link;
        newLink.dispatchEvent(new Event("input", { bubbles: true, cancelable: true }));

        selectionUtils.current.collapseToEnd();
        updateState();
    };

    const removeLink = (): void => {
        document.execCommand("unlink");
        updateState();
    };

    const link = new SelectionUtils().findParentTag("A");
    return (
        <>
            {state.selectionActive ? (
                <Card
                    className="InlineEditor"
                    onClick={(e) => {
                        e.stopPropagation();
                    }}
                    sx={{
                        display: selectionVisible ? "block" : "none",
                        position: "absolute",
                        zIndex: 100,
                        transform: `translate(${pos.x}px, ${pos.y}px)`,
                        top: 0,
                        left: 0,
                    }}
                >
                    <ToggleButtonGroup value={buttonState} aria-label="text formatting" size="small">
                        <ToggleButton value="bold" aria-label="bold" onClick={toggleBold}>
                            <FormatBoldIcon />
                        </ToggleButton>

                        <ToggleButton value="italic" aria-label="italic" onClick={toggleItalic}>
                            <FormatItalicIcon />
                        </ToggleButton>

                        <ToggleButton
                            value="link"
                            aria-label="link"
                            onClick={() => {
                                if (link) {
                                    removeLink();
                                    return;
                                }

                                setInsertLinkModal(true);
                                return;
                            }}
                        >
                            {link ? <LinkOff /> : <InsertLink />}
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Card>
            ) : null}

            <Dialog open={insertLinkModal}>
                <DialogTitle>Add Link</DialogTitle>
                <DialogContent>
                    <TextField autoFocus fullWidth inputRef={textRef} />
                </DialogContent>

                <DialogActions>
                    <Button
                        onClick={() => {
                            setInsertLinkModal(false);
                            selectionUtils.current.restore();
                        }}
                        color="primary"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={() => {
                            insertLink(textRef.current?.value || "");
                            setInsertLinkModal(false);
                        }}
                        color="primary"
                    >
                        Add
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default InlineEditor;
